import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, Checkbox, useToast,
} from '@chakra-ui/react';
import {
  ImmAtencion, crearAtencion, actualizarAtencion,
} from '../../../api/immApi';
import {
  SectionTitle, Field, ExpedienteSelect, IMM_COLOR, IMM_COLOR_HOVER, fmt,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmAtencion;
  onGuardado?: (a: ImmAtencion) => void;
  onCancelar?: () => void;
}

const MEDIOS_LLEGADA = ['Presencial', 'Telefono', 'WhatsApp', 'Correo', 'Canalizacion', 'PortalCiudadano'];
const ESTATUS_OPCIONES = ['Registrada', 'Concluida'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioAtencion: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId);

  const [f, setF] = useState<Partial<ImmAtencion>>({
    fecha: data?.fecha ? data.fecha.split('T')[0] : hoy(),
    hora: data?.hora || '',
    medioLlegada: data?.medioLlegada || '',
    motivo: data?.motivo || '',
    descripcion: data?.descripcion || '',
    tipoApoyo: data?.tipoApoyo || '',
    area: data?.area || '',
    servidorPublico: data?.servidorPublico || '',
    observaciones: data?.observaciones || '',
    orientacion: data?.orientacion ?? false,
    asesoria: data?.asesoria ?? false,
    acompanamiento: data?.acompanamiento ?? false,
    canalizacion: data?.canalizacion ?? false,
    acudioAcompanada: data?.acudioAcompanada ?? false,
    asistioConMenores: data?.asistioConMenores ?? false,
    datosAcompanantes: data?.datosAcompanantes || '',
    estatus: data?.estatus || 'Registrada',
  });

  const set = (campo: keyof ImmAtencion, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!expedienteId) {
      toast({ title: 'Selecciona un expediente', status: 'warning', duration: 3000 });
      return;
    }
    if (!f.fecha) {
      toast({ title: 'La fecha es obligatoria', status: 'warning', duration: 3000 });
      return;
    }
    if (!f.medioLlegada) {
      toast({ title: 'El medio de llegada es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmAtencion> = { ...f, expedienteId };
      const res = modo === 'edit' && data
        ? await actualizarAtencion(data.id, payload)
        : await crearAtencion(payload);
      toast({ title: modo === 'edit' ? 'Atención actualizada' : 'Atención registrada', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar la atención.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const servicios = (a?: ImmAtencion) => {
    if (!a) return '—';
    const list = [
      a.orientacion && 'Orientación',
      a.asesoria && 'Asesoría',
      a.acompanamiento && 'Acompañamiento',
      a.canalizacion && 'Canalización',
    ].filter(Boolean);
    return list.length ? list.join(', ') : '—';
  };

  if (modo === 'view' && data) {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la atención</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Expediente" value={data.expediente?.folio ?? ('#' + data.expedienteId)} />
          <Field label="Fecha" value={fmt(data.fecha)} />
          <Field label="Hora" value={data.hora} />
          <Field label="Medio de llegada" value={data.medioLlegada} />
          <Field label="Motivo" value={data.motivo} />
          <Field label="Tipo de apoyo" value={data.tipoApoyo} />
          <Field label="Área" value={data.area} />
          <Field label="Servidor público" value={data.servidorPublico} />
          <Field label="Estatus" value={data.estatus} />
        </SimpleGrid>

        <SectionTitle>Descripción y servicios</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
          <Field label="Descripción" value={data.descripcion} />
          <Field label="Servicios otorgados" value={servicios(data)} />
          <Field label="Acudió acompañada" value={data.acudioAcompanada ? 'Sí' : 'No'} />
          <Field label="Asistió con menores" value={data.asistioConMenores ? 'Sí' : 'No'} />
          <Field label="Datos de acompañantes" value={data.datosAcompanantes} />
          <Field label="Observaciones" value={data.observaciones} />
        </SimpleGrid>

        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la atención</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Expediente</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} isDisabled={modo !== 'new'} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Hora</FormLabel>
          <Input type="time" value={f.hora || ''} onChange={(e) => set('hora', e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Medio de llegada</FormLabel>
          <Select placeholder="Selecciona" value={f.medioLlegada || ''} onChange={(e) => set('medioLlegada', e.target.value)}>
            {MEDIOS_LLEGADA.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Motivo</FormLabel>
          <Input value={f.motivo || ''} onChange={(e) => set('motivo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de apoyo</FormLabel>
          <Input value={f.tipoApoyo || ''} onChange={(e) => set('tipoApoyo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Área</FormLabel>
          <Input value={f.area || ''} onChange={(e) => set('area', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Servidor público</FormLabel>
          <Input value={f.servidorPublico || ''} onChange={(e) => set('servidorPublico', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Registrada'} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_OPCIONES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Descripción</SectionTitle>
      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea rows={3} value={f.descripcion || ''} onChange={(e) => set('descripcion', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Servicios otorgados</SectionTitle>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Checkbox isChecked={!!f.orientacion} onChange={(e) => set('orientacion', e.target.checked)}>Orientación</Checkbox>
        <Checkbox isChecked={!!f.asesoria} onChange={(e) => set('asesoria', e.target.checked)}>Asesoría</Checkbox>
        <Checkbox isChecked={!!f.acompanamiento} onChange={(e) => set('acompanamiento', e.target.checked)}>Acompañamiento</Checkbox>
        <Checkbox isChecked={!!f.canalizacion} onChange={(e) => set('canalizacion', e.target.checked)}>Canalización</Checkbox>
        <Checkbox isChecked={!!f.acudioAcompanada} onChange={(e) => set('acudioAcompanada', e.target.checked)}>Acudió acompañada</Checkbox>
        <Checkbox isChecked={!!f.asistioConMenores} onChange={(e) => set('asistioConMenores', e.target.checked)}>Asistió con menores</Checkbox>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1 }} spacing={4} mt={2}>
        <FormControl>
          <FormLabel fontSize="sm">Datos de acompañantes</FormLabel>
          <Textarea rows={2} value={f.datosAcompanantes || ''} onChange={(e) => set('datosAcompanantes', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={2} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar atención
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioAtencion;
